import React, { useState, useEffect, useCallback, useRef } from "react";
import {
  Banner,
  Button,
  Card,
  DataTable,
  Modal,
  Pagination,
  Select,
  Stack,
  TextField,
  TextStyle,
  Spinner,
  FormLayout
} from "@shopify/polaris";
import { fetchData, fetcher } from "util/fetch";
import { useAdmin } from "context/admin";

export default function StoreBottleDepositsTable() {
  const admin = useAdmin();
  const [overrides, setOverrides] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [shopFilter, setShopFilter] = useState("");
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalOverrides, setTotalOverrides] = useState(0);

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingOverride, setEditingOverride] = useState(null);

  const [formData, setFormData] = useState({
    shopify_domain: "",
    upc: "",
    brand: "",
    deposit_cents_per_unit: "0",
    crf_cents_per_unit: "0",
    pack_count: "1",
    notes: ""
  });
  const [formSaving, setFormSaving] = useState(false);

  const bulkFileRef = useRef(null);
  const [bulkModalOpen, setBulkModalOpen] = useState(false);
  const [bulkShop, setBulkShop] = useState("");
  const [bulkFile, setBulkFile] = useState(null);
  const [bulkUploading, setBulkUploading] = useState(false);
  const [importBanner, setImportBanner] = useState(null);

  useEffect(() => {
    if (!admin.loadingShops && !admin.shops) {
      admin.fetchShops();
    }
  }, [admin]);

  const shopOptions = [
    { label: "All shops", value: "" },
    ...((admin.shops || []).map((s) => ({
      label: s.shop_name || s.shopify_domain,
      value: s.shopify_domain
    })))
  ];

  const shopOptionsNoAll = shopOptions.filter((o) => o.value !== "");

  const loadOverrides = useCallback(() => {
    setLoading(true);
    const params = new URLSearchParams({ page });
    if (searchQuery) params.set("q", searchQuery);
    if (shopFilter) params.set("shopify_domain", shopFilter);

    fetchData(
      `/api/store_bottle_deposits?${params}`,
      ({ overrides, total, total_pages }) => {
        setOverrides(overrides);
        setTotalOverrides(total);
        setTotalPages(total_pages);
      },
      (err) => console.error(err),
      () => setLoading(false)
    );
  }, [page, searchQuery, shopFilter]);

  useEffect(() => {
    const timer = setTimeout(() => {
      loadOverrides();
    }, 300);
    return () => clearTimeout(timer);
  }, [loadOverrides]);

  const handleOpenModal = (override = null) => {
    if (override) {
      setEditingOverride(override);
      setFormData({
        shopify_domain: override.shopify_domain,
        upc: override.upc,
        brand: override.brand || "",
        deposit_cents_per_unit: override.deposit_cents_per_unit.toString(),
        crf_cents_per_unit: override.crf_cents_per_unit.toString(),
        pack_count: override.pack_count.toString(),
        notes: override.notes || ""
      });
    } else {
      setEditingOverride(null);
      setFormData({
        shopify_domain: shopFilter || "",
        upc: "",
        brand: "",
        deposit_cents_per_unit: "0",
        crf_cents_per_unit: "0",
        pack_count: "1",
        notes: ""
      });
    }
    setIsModalOpen(true);
  };

  const handleSave = async () => {
    setFormSaving(true);
    try {
      const path = editingOverride
        ? `/api/store_bottle_deposits/${editingOverride.id}`
        : "/api/store_bottle_deposits";

      const method = editingOverride ? "PUT" : "POST";

      const res = await fetcher(path, {
        method,
        headers: {
          "Content-Type": "application/json"
        },
        body: JSON.stringify({ store_bottle_deposit: formData })
      });

      if (!res.ok) {
        const errorData = await res.json();
        alert(errorData.errors?.join(", ") || "Failed to save");
        return;
      }

      setIsModalOpen(false);
      loadOverrides();
    } catch (err) {
      console.error(err);
      alert("An error occurred while saving");
    } finally {
      setFormSaving(false);
    }
  };

  const openBulkModal = () => {
    setBulkShop(shopFilter || (admin.shops?.[0]?.shopify_domain ?? ""));
    setBulkFile(null);
    if (bulkFileRef.current) bulkFileRef.current.value = "";
    setBulkModalOpen(true);
  };

  const handleBulkImport = async () => {
    if (!bulkShop) {
      alert("Select a shop.");
      return;
    }
    if (!bulkFile) {
      alert("Choose a CSV file.");
      return;
    }

    setBulkUploading(true);
    setImportBanner(null);
    try {
      const fd = new FormData();
      fd.append("shopify_domain", bulkShop);
      fd.append("file", bulkFile);

      const res = await fetcher("/api/store_bottle_deposits/bulk_import", {
        method: "POST",
        body: fd
      });

      const data = await res.json().catch(() => ({}));

      if (!res.ok) {
        const msg = (data.errors && data.errors.join("; ")) || "Import failed.";
        setImportBanner({ status: "critical", title: msg, details: data.errors || [] });
        return;
      }

      const title = `Imported ${data.created} new, updated ${data.updated} existing.`;
      const hasRowErrors = Array.isArray(data.errors) && data.errors.length > 0;
      setImportBanner({
        status: hasRowErrors ? "warning" : "success",
        title,
        details: hasRowErrors ? data.errors : []
      });
      setBulkModalOpen(false);
      setBulkFile(null);
      if (bulkFileRef.current) bulkFileRef.current.value = "";
      setPage(1);
      loadOverrides();
    } catch (err) {
      console.error(err);
      setImportBanner({ status: "critical", title: "Import failed.", details: [] });
    } finally {
      setBulkUploading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this override?")) return;

    try {
      await fetcher(`/api/store_bottle_deposits/${id}`, {
        method: "DELETE"
      });
      loadOverrides();
    } catch (err) {
      console.error(err);
      alert("Failed to delete override");
    }
  };

  const formatCents = (cents) => `$${(cents / 100).toFixed(2)}`;

  const shopLabel = (domain) => {
    const shop = (admin.shops || []).find((s) => s.shopify_domain === domain);
    return shop?.shop_name || domain;
  };

  const rows = overrides.map((o) => [
    shopLabel(o.shopify_domain),
    o.upc,
    o.brand || "—",
    o.pack_count,
    formatCents(o.deposit_cents_per_unit),
    formatCents(o.crf_cents_per_unit),
    formatCents(o.effective_total_cents),
    o.notes || "—",
    <Stack spacing="tight">
      <Button size="slim" onClick={() => handleOpenModal(o)}>Edit</Button>
      <Button size="slim" destructive onClick={() => handleDelete(o.id)}>Delete</Button>
    </Stack>
  ]);

  return (
    <Card
      title={`Store Overrides (${totalOverrides})`}
      actions={[
        { content: "Add override", onAction: () => handleOpenModal() },
        { content: "Bulk import CSV", onAction: openBulkModal }
      ]}
    >
      {importBanner && (
        <Card.Section>
          <Banner
            status={importBanner.status}
            onDismiss={() => setImportBanner(null)}
          >
            <p>{importBanner.title}</p>
            {importBanner.details?.length > 0 && (
              <ul style={{ margin: "0.5rem 0 0", paddingLeft: "1.25rem" }}>
                {importBanner.details.map((line, i) => (
                  <li key={i}>{line}</li>
                ))}
              </ul>
            )}
          </Banner>
        </Card.Section>
      )}

      <Card.Section>
        <Stack>
          <Stack.Item fill>
            <TextField
              label="Search"
              labelHidden
              placeholder="Search by UPC or brand…"
              value={searchQuery}
              onChange={(val) => { setSearchQuery(val); setPage(1); }}
              clearButton
              onClearButtonClick={() => { setSearchQuery(""); setPage(1); }}
            />
          </Stack.Item>
          <Select
            label="Shop"
            labelHidden
            options={shopOptions}
            value={shopFilter}
            onChange={(val) => { setShopFilter(val); setPage(1); }}
          />
        </Stack>
      </Card.Section>

      <Card.Section flush>
        {loading ? (
          <div style={{ padding: "2rem", textAlign: "center" }}>
            <Spinner size="small" />
          </div>
        ) : overrides.length === 0 ? (
          <div style={{ padding: "2rem", textAlign: "center" }}>
            <TextStyle variation="subdued">No store overrides found.</TextStyle>
          </div>
        ) : (
          <DataTable
            columnContentTypes={["text", "text", "text", "numeric", "numeric", "numeric", "numeric", "text", "text"]}
            headings={["Shop", "UPC", "Brand", "Pack Count", "Deposit/unit", "CRF/unit", "Total Fee", "Notes", "Actions"]}
            rows={rows}
          />
        )}
      </Card.Section>

      {totalPages > 1 && (
        <Card.Section>
          <Stack distribution="center">
            <Pagination
              hasPrevious={page > 1}
              onPrevious={() => setPage((p) => p - 1)}
              hasNext={page < totalPages}
              onNext={() => setPage((p) => p + 1)}
            />
          </Stack>
        </Card.Section>
      )}

      <Modal
        open={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        title={editingOverride ? "Edit Store Override" : "Add Store Override"}
        primaryAction={{
          content: "Save",
          onAction: handleSave,
          loading: formSaving
        }}
        secondaryActions={[
          { content: "Cancel", onAction: () => setIsModalOpen(false) }
        ]}
      >
        <Modal.Section>
          <FormLayout>
            <Select
              label="Shop"
              options={shopOptions.filter((o) => o.value !== "")}
              value={formData.shopify_domain}
              onChange={(val) => setFormData({ ...formData, shopify_domain: val })}
              disabled={!!editingOverride}
            />

            <FormLayout.Group>
              <TextField
                label="UPC"
                value={formData.upc}
                onChange={(val) => setFormData({ ...formData, upc: val })}
                disabled={!!editingOverride}
              />
              <TextField
                label="Brand / Title"
                value={formData.brand}
                onChange={(val) => setFormData({ ...formData, brand: val })}
              />
            </FormLayout.Group>

            <FormLayout.Group>
              <TextField
                label="Deposit (cents/unit)"
                type="number"
                value={formData.deposit_cents_per_unit}
                onChange={(val) => setFormData({ ...formData, deposit_cents_per_unit: val })}
              />
              <TextField
                label="CRF (cents/unit)"
                type="number"
                value={formData.crf_cents_per_unit}
                onChange={(val) => setFormData({ ...formData, crf_cents_per_unit: val })}
              />
            </FormLayout.Group>

            <TextField
              label="Pack Count"
              type="number"
              value={formData.pack_count}
              onChange={(val) => setFormData({ ...formData, pack_count: val })}
              helpText="Number of containers in this product. Default is 1 for single items."
            />

            <TextField
              label="Notes"
              value={formData.notes}
              onChange={(val) => setFormData({ ...formData, notes: val })}
              multiline={2}
            />
          </FormLayout>
        </Modal.Section>
      </Modal>

      <Modal
        open={bulkModalOpen}
        onClose={() => setBulkModalOpen(false)}
        title="Bulk import store overrides"
        primaryAction={{
          content: "Import",
          onAction: handleBulkImport,
          loading: bulkUploading
        }}
        secondaryActions={[
          { content: "Cancel", onAction: () => setBulkModalOpen(false) }
        ]}
      >
        <Modal.Section>
          <Stack vertical>
            <Select
              label="Shop"
              options={shopOptionsNoAll}
              value={bulkShop}
              onChange={setBulkShop}
              placeholder="Select shop"
            />

            <input
              ref={bulkFileRef}
              type="file"
              accept=".csv,text/csv"
              style={{ display: "none" }}
              onChange={(e) => setBulkFile(e.target.files?.[0] ?? null)}
            />
            <Stack spacing="tight">
              <Button onClick={() => bulkFileRef.current?.click()}>
                Choose CSV file
              </Button>
              <TextStyle variation="subdued">
                {bulkFile ? bulkFile.name : "No file selected"}
              </TextStyle>
            </Stack>

            <TextStyle variation="subdued">
              Header row required:
              {" "}
              <code>upc</code>
              ,
              {" "}
              <code>deposit_cents_per_unit</code>
              {" "}
              (optional:
              {" "}
              <code>crf_cents_per_unit</code>
              ,
              {" "}
              <code>pack_count</code>
              ,
              {" "}
              <code>brand</code>
              ,
              {" "}
              <code>notes</code>
              ). UPCs are normalized (leading zeros stripped) to match checkout SKUs.
            </TextStyle>
          </Stack>
        </Modal.Section>
      </Modal>
    </Card>
  );
}
